import {
  entityEdgesWithoutSubset,
  moveToFulfilled,
  moveToPending,
  moveToPendingIfNotFulfilled,
  moveToRejected,
  objectWithoutKeys,
  remove,
  update,
} from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  CancelGenerateCuratedThreadsAction,
  CancelGenerateCuratedThreadsFulfilledAction,
  CuratedThreadListLoadAction,
  CuratedThreadListLoadFulfilledAction,
  GenerateCuratedThreadsAction,
  ToggleAllCuratedThreadListItemsAction,
  ToggleCuratedThreadListItemAction,
  TogglePauseGenerateCuratedThreadsAction,
} from '../../domains/curatedThreadList/actions.types';
import type { DeleteThreadFulfilledAction } from '../../domains/deleteThread/actions.types';
import type { PublishDraftThreadsAction } from '../../domains/publishDraftThreads/actions.types';
import type { Thread, ThreadEdge } from '../types';

import type { CuratedThreadListEdge, CuratedThreadLists } from './types';

export type CuratedThreadListsAction =
  | CuratedThreadListLoadAction
  | GenerateCuratedThreadsAction
  | DeleteThreadFulfilledAction
  | PublishDraftThreadsAction
  | CancelGenerateCuratedThreadsAction
  | TogglePauseGenerateCuratedThreadsAction
  | ToggleCuratedThreadListItemAction
  | ToggleAllCuratedThreadListItemsAction;

const initialState: CuratedThreadLists = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithRejectedCuratedThreadList = (
  state: CuratedThreadLists,
  curatedThreadListId: string,
  error: unknown
) => ({
  ...state,
  fulfilled: {
    ...state.fulfilled,
    byId: objectWithoutKeys(state.fulfilled.byId, [curatedThreadListId]),
  },
  rejected: {
    ...state.rejected,
    byId: {
      ...state.rejected.byId,
      [curatedThreadListId]: error,
    },
  },
});

const stateWithFulfilledCuratedThreadList = (
  state: CuratedThreadLists,
  { curatedThreadListId, entities }: CuratedThreadListLoadFulfilledAction['payload']
) => {
  const existingCuratedThreadList = state.fulfilled.byId[curatedThreadListId];
  const payloadCuratedThreadList = entities.curatedThreadLists[curatedThreadListId];

  if (!existingCuratedThreadList) {
    return moveToFulfilled(state, {
      [curatedThreadListId]: { ...payloadCuratedThreadList, dispatchCount: 1 },
    });
  }

  const updatedCuratedThreadList = {
    ...payloadCuratedThreadList,
    dispatchCount: (existingCuratedThreadList.dispatchCount || 0) + 1,
  };

  return update(state, curatedThreadListId, updatedCuratedThreadList);
};

const removeThreadFromAllCuratedThreadLists = (
  state: CuratedThreadLists,
  { threadId }: DeleteThreadFulfilledAction['payload']
) =>
  Object.keys(state.fulfilled.byId).reduce(
    (updatedState, curatedThreadListId) =>
      removeThreadsFromCuratedThreadList(updatedState, [{ nodeId: threadId }], curatedThreadListId),
    state
  );

const removePublishedThreadsFromAllCuratedThreadLists = (state: CuratedThreadLists, threadIds: Thread['id'][]) =>
  threadIds.reduce(
    (updatedState: CuratedThreadLists, threadId: Thread['id']) =>
      removeThreadFromAllCuratedThreadLists(updatedState, { threadId }),
    state
  );

const removeThreadsFromCuratedThreadList = (
  state: CuratedThreadLists,
  threadEdges: ThreadEdge[],
  curatedThreadListId: string
) => {
  const curatedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!curatedThreadList || (curatedThreadList.edges && curatedThreadList.edges.length === 0)) {
    return state;
  }

  const nonNullCuratedThreadListEdges =
    curatedThreadList.edges?.filter((edge): edge is CuratedThreadListEdge => edge != null) ?? [];

  const otherThreadEdges = entityEdgesWithoutSubset<Thread>(nonNullCuratedThreadListEdges, threadEdges);

  if (otherThreadEdges.length === nonNullCuratedThreadListEdges.length) {
    return state;
  }

  const updatedCuratedthreadList = {
    ...curatedThreadList,
    edges: otherThreadEdges,
  };

  return update(state, curatedThreadListId, updatedCuratedthreadList);
};

const removeCuratedThreadList = (
  state: CuratedThreadLists,
  { curatedThreadListId }: CancelGenerateCuratedThreadsFulfilledAction['payload']
) => remove(state, [curatedThreadListId]);

const stateWithToggledPauseCuratedThreadList = (
  state: CuratedThreadLists,
  { curatedThreadListId }: TogglePauseGenerateCuratedThreadsAction['payload']
) => {
  const curatedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!curatedThreadList) {
    return state;
  }

  const updatedCuratedThreadList = {
    ...curatedThreadList,
    isPaused: !curatedThreadList.isPaused,
  };

  return update(state, curatedThreadListId, updatedCuratedThreadList);
};

const stateWithToggledCuratedThreadListItem = (
  state: CuratedThreadLists,
  { curatedThreadListId, threadId }: ToggleCuratedThreadListItemAction['payload']
) => {
  const curatedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!curatedThreadList?.edges) {
    return state;
  }

  const updatedEdges = curatedThreadList.edges.map((edge) => {
    if (edge?.nodeId === threadId) {
      return {
        ...edge,
        isSelected: !edge.isSelected,
      };
    }

    return edge;
  });

  const updatedCuratedThreadList = {
    ...curatedThreadList,
    edges: updatedEdges,
  };

  return update(state, curatedThreadListId, updatedCuratedThreadList);
};

const stateWithAllToggledCuratedThreadListItems = (
  state: CuratedThreadLists,
  { curatedThreadListId }: ToggleAllCuratedThreadListItemsAction['payload']
) => {
  const curatedThreadList = state.fulfilled.byId[curatedThreadListId];

  if (!curatedThreadList?.edges) {
    return state;
  }

  const edgesNotSelected = curatedThreadList.edges.some((edge) => edge != null && !edge.isSelected);

  const updatedEdges = curatedThreadList.edges
    .filter((edge): edge is CuratedThreadListEdge => edge?.nodeId !== undefined)
    .map((edge) => ({
      ...edge,
      isSelected: edgesNotSelected,
      nodeId: edge.nodeId,
    }));

  const updatedCuratedThreadList = {
    ...curatedThreadList,
    edges: updatedEdges,
  };

  return update(state, curatedThreadListId, updatedCuratedThreadList);
};

const reducer: Reducer<CuratedThreadLists, CuratedThreadListsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CURATED_THREAD_LIST_LOAD.PENDING':
      return moveToPendingIfNotFulfilled(state, action.payload.curatedThreadListId);
    case 'CURATED_THREAD_LIST_LOAD.REJECTED':
      return stateWithRejectedCuratedThreadList(state, action.payload.curatedThreadListId, action.payload.error);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return stateWithFulfilledCuratedThreadList(state, action.payload);
    case 'THREAD.DELETE_FULFILLED':
      return removeThreadFromAllCuratedThreadLists(state, action.payload);
    case 'PUBLISH_DRAFT_THREADS.FULFILLED':
      return removePublishedThreadsFromAllCuratedThreadLists(state, action.payload.threadIds);
    case 'CANCEL_GENERATE_CURATED_THREADS.PENDING':
      return moveToPending(state, [action.payload.curatedThreadListId]);
    case 'CANCEL_GENERATE_CURATED_THREADS.FULFILLED':
      return removeCuratedThreadList(state, action.payload);
    case 'CANCEL_GENERATE_CURATED_THREADS.REJECTED':
      return moveToRejected(state, [action.payload.curatedThreadListId], action.payload.error);
    case 'TOGGLE_PAUSE.GENERATE_CURATED_THREADS':
      return stateWithToggledPauseCuratedThreadList(state, action.payload);
    case 'TOGGLE.CURATED_THREAD_LIST_ITEM':
      return stateWithToggledCuratedThreadListItem(state, action.payload);
    case 'TOGGLE_ALL.CURATED_THREAD_LIST_ITEM':
      return stateWithAllToggledCuratedThreadListItems(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
